import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { CircularProgress, Grid, Typography } from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import { Modal } from '@refera/ui-web'
import { saveAs } from 'file-saver'

import Button from '_components/button'
import { downloadBudgetPdf } from '_services/budget'
import { useToast } from '_/hooks/use-toast'
import useBudgetPdfFileName from '_hooks/use-budget-pdf-file-name'

import useStyles from './styles'

const ModalConfirmInstallment = ({ open, onCancel, budgetId }) => {
  const styles = useStyles()
  const { showToast } = useToast()
  const pdfFileName = useBudgetPdfFileName({ budgetId })

  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [pdfBlob, setPdfBlob] = useState(null)

  useEffect(() => {
    if (!open || !budgetId) return undefined

    let isCancelled = false
    setIsLoading(true)
    setHasError(false)
    setPdfBlob(null)

    downloadBudgetPdf(budgetId)
      .then(response => {
        if (!isCancelled) setPdfBlob(response.data)
      })
      .catch(() => {
        if (!isCancelled) setHasError(true)
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false)
      })

    return () => {
      isCancelled = true
    }
  }, [open, budgetId])

  const handleDownload = () => {
    if (!pdfBlob) return

    try {
      saveAs(pdfBlob, pdfFileName)
      onCancel()
    } catch (error) {
      showToast({ type: 'error', message: 'Falha ao baixar o PDF do orçamento.' })
    }
  }

  return (
    <Modal
      className={styles.modal}
      title="PDF do orçamento"
      onBackdropClick={() => onCancel()}
      onClose={() => onCancel()}
      open={open}
      actionsButtons={
        <Grid className={styles.modalButtons}>
          <Button
            className={classnames(styles.modalButton, styles.cancelButton)}
            color="red"
            variant="outlined"
            onClick={() => onCancel()}
          >
            Cancelar
          </Button>
          <Button
            className={styles.modalButton}
            color="primary"
            variant="contained"
            onClick={handleDownload}
            disabled={isLoading || hasError || !pdfBlob}
          >
            Baixar
          </Button>
        </Grid>
      }
    >
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        className={styles.modalContent}
      >
        {isLoading && (
          <>
            <CircularProgress size={40} />
            <Typography>Gerando PDF...</Typography>
          </>
        )}
        {hasError && (
          <Typography>
            Falha ao gerar o PDF do orçamento.
            <br />
            Feche e tente novamente.
          </Typography>
        )}
        {!isLoading && !hasError && pdfBlob && (
          <>
            <CheckCircleOutlineIcon className={styles.successIcon} />
            <Typography>PDF pronto!</Typography>
          </>
        )}
      </Grid>
    </Modal>
  )
}

ModalConfirmInstallment.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default ModalConfirmInstallment
